import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CustomButton from '../CustomButton/CustomButton';
import { Apple, Google, LinkedIn } from '../svgs/Svgs';
import DottedLoading from '../DottedLoading/DottedLoading';
import { linkSocialAccountAPI } from '../../redux/features/socialAccountsSlice';

const SocialAccountModal = ({ isOpen, onClose, provider, onSuccess }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    provider: provider || '',
    provider_id: '',
    email: '',
    profile_url: '',
    name: '',
    otp: '', // For Google OTP validation
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  // Reset form when modal opens/closes or provider changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        provider: provider || '',
        provider_id: '',
        email: '',
        profile_url: '',
        name: '',
        otp: '',
      });
      setErrors({});
      setIsFormValid(false);
      setOtpSent(false);
    }
  }, [isOpen, provider]);

  const validateEmail = () => {
    if (!formData.email || !/^[^\s@]+@gmail\.com$/.test(formData.email)) {
      setErrors({ email: 'Please enter a valid Gmail address.' });
      setIsFormValid(false);
    } else {
      setErrors({});
      setIsFormValid(true);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (provider === 'linkedin') {
      if (!formData.profile_url || !/^https?:\/\/(www\.)?linkedin\.com\/.+/.test(formData.profile_url)) {
        newErrors.profile_url = 'Please enter a valid LinkedIn profile URL.';
      }
      if (!formData.name || formData.name.trim().length < 2) {
        newErrors.name = 'Please enter your full name.';
      }
    }

    if (provider === 'apple') {
      if (!formData.provider_id || formData.provider_id.trim().length < 3) {
        newErrors.provider_id = 'Please enter a valid Apple ID.';
      }
      if (!formData.name || formData.name.trim().length < 2) {
        newErrors.name = 'Please enter your full name.';
      }
    }

    setErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  };

  const handleSendOtp = () => {
    if (isFormValid) {
      // Simulate sending OTP
      setOtpSent(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Re-validate form on input change
    if ((name === 'profile_url' && provider === 'linkedin') || 
        (name === 'provider_id' && provider === 'apple') ||
        (name === 'name' && (provider === 'linkedin' || provider === 'apple'))) {
      validateForm();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For Google, check OTP
    if (provider === 'google' && (!otpSent || formData.otp !== '123456')) {
      setErrors({ otp: 'Invalid OTP. Please try again.' });
      return;
    }

    // For LinkedIn and Apple, validate form
    if ((provider === 'linkedin' || provider === 'apple') && !isFormValid) {
      return;
    }

    setIsLoading(true);
    try {
      // Prepare data for API
      const accountData = {
        provider: formData.provider,
        provider_id: formData.provider_id || formData.email, // Use email as provider_id for Google
        email: provider === 'google' ? formData.email : null, // Only send email for Google
        name: formData.name,
        profile_url: formData.profile_url
      };

      // Dispatch action to link account
      const result = await dispatch(linkSocialAccountAPI(accountData));
      
      if (result.type.endsWith('fulfilled')) {
        onSuccess();
        onClose();
      } else {
        setErrors({ submit: result.payload || 'Failed to link account' });
      }
    } catch (error) {
      console.error(error);
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'linkedin':
        return <LinkedIn />;
      case 'google':
        return <Google />;
      case 'apple':
        return <Apple />;
      default:
        return '';
    }
  };

  const getProviderName = (provider) => {
    switch (provider) {
      case 'linkedin':
        return 'LinkedIn';
      case 'google':
        return 'Google';
      case 'apple':
        return 'apple';
      default:
        return provider;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center gap-2">
              {getProviderIcon(formData.provider)}
              Link {getProviderName(formData.provider)} Account
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {errors.submit && (
                <div className="alert alert-danger" role="alert">
                  {errors.submit}
                </div>
              )}

              {provider === 'linkedin' && (
                <>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                    />
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="profile_url" className="form-label">
                      LinkedIn Profile URL <span className="text-danger">*</span>
                    </label>
                    <input
                      type="url"
                      className={`form-control ${errors.profile_url ? 'is-invalid' : ''}`}
                      id="profile_url"
                      name="profile_url"
                      value={formData.profile_url}
                      onChange={handleInputChange}
                      placeholder="https://www.linkedin.com/in/your-profile"
                    />
                    {errors.profile_url && (
                      <div className="invalid-feedback">{errors.profile_url}</div>
                    )}
                  </div>
                </>
              )}

              {provider === 'google' && (
                <div>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      Gmail Account <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={validateEmail}
                      placeholder="Enter your Gmail account"
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>

                  {!otpSent && (
                    <button type="button" className="btn btn-primary" onClick={handleSendOtp}>
                      Send OTP
                    </button>
                  )}

                  {otpSent && (
                    <div className="mb-3">
                      <label htmlFor="otp" className="form-label">
                        OTP <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.otp ? 'is-invalid' : ''}`}
                        id="otp"
                        name="otp"
                        value={formData.otp}
                        onChange={handleInputChange}
                        placeholder="Enter the OTP sent to your Gmail"
                      />
                      {errors.otp && (
                        <div className="invalid-feedback">{errors.otp}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {provider === 'apple' && (
                <>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                    />
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="provider_id" className="form-label">
                      Apple ID <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.provider_id ? 'is-invalid' : ''}`}
                      id="provider_id"
                      name="provider_id"
                      value={formData.provider_id}
                      onChange={handleInputChange}
                      placeholder="Enter your Apple ID"
                    />
                    {errors.provider_id && (
                      <div className="invalid-feedback">{errors.provider_id}</div>
                    )}
                  </div>
                </>
              )}

            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <>
                    <DottedLoading />
                  </>
                ) : (
                  'Link Account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SocialAccountModal;
